<template>
  <div ref="wrapRef" class="mts-page-container" v-bind="$attrs">
    <MHeader
      v-if="!hideHeader"
      v-bind="{ isSubPage, showUserAndTime, showBack, backEmit, beforeBackFn, backFn }"
    >
      <template #headerLeft>
        <slot name="headerLeft" v-bind="{ pageTitle }">
          <div v-if="isSubPage" class="text-22px font-500 flex-shrink-0">{{ t(pageTitle) }}</div>
        </slot>
      </template>
      <template #headerCenter>
        <slot name="headerCenter">
          <div v-if="!isSubPage" class="text-22px font-500">{{ t(pageTitle) }}</div>
        </slot>
      </template>
      <template #headerRight>
        <slot name="headerRight"></slot>
      </template>
    </MHeader>

    <PullRefresh
      v-if="pullRefresh"
      v-model="refreshing"
      :class="['mts-page-content', contentClass, { 'has-footer': showFooter }]"
      @touchmove.capture="onTouchMove"
      @touchstart.capture="onTouchStart"
      @refresh="onPullRefresh"
    >
      <slot></slot>
      <div class="h-full">
        <slot name="pullArea" v-bind="{ refreshDone }"></slot>
      </div>
    </PullRefresh>
    <div v-else :class="['mts-page-content', contentClass, { 'has-footer': showFooter }]">
      <slot></slot>
    </div>

    <slot name="footer">
      <div
        v-if="showFooter"
        v-bind="footerProps"
        class="h-60px leading-40px px-16px flex items-center justify-between"
      >
        <div class="gap-x-12px flex">
          <slot name="footerLeft"></slot>
        </div>
        <div class="gap-x-12px flex">
          <slot name="footerRight"></slot>
        </div>
      </div>
    </slot>
  </div>
</template>

<script setup lang="ts">
  import PullRefresh from 'pull-refresh-vue3';
  import { computed, provide, ref, useTemplateRef } from 'vue';
  import { useRoute } from 'vue-router';
  import { findScrollingElement } from '@mts-fe/hooks';
  import { MHeader } from '@mts/components/Header';
  import { PageHasFooterKey } from '@mts/enums/pageEnum';
  import { t } from '@mts/hooks/web/useI18n';
  import { router } from '@/router';

  defineOptions({ name: 'MPage', inheritAttrs: false });

  const props = withDefaults(
    defineProps<{
      /** 页面标题 */
      title?: string;
      /** 隐藏头部 */
      hideHeader?: boolean;
      /** 二级页面 */
      isSubPage?: boolean;
      /** 下拉刷新，默认关闭。支持三种形式：
       * - boolean：开关，通过 @refresh 事件处理，需手动调用 refreshDone()
       * - async 函数：自动调用并管理 refreshing 状态 */
      pullRefresh?:
        | boolean
        | (() => Promise<void>)
        | {
            /** @deprecated pullArea 内禁用触摸冒泡，阻止下拉刷新 */
            noTouch?: boolean;
          };
      contentClass?: string;
      /** 底部栏，默认关闭*/
      showFooter?: { class?: any } | boolean;
      showUserAndTime?: boolean;
      showBack?: boolean;
      backEmit?: string;
      /** 默认返回事件前，false取消执行 */
      beforeBackFn?: () => Promise<void | false>;
      /** @deprecated 回到上级/上一页 */
      backFn?: () => void;
    }>(),
    { showBack: true },
  );

  defineSlots<{
    headerLeft?: (props: { pageTitle: string }) => any;
    headerCenter?: () => any;
    headerRight?: () => any;
    default?: () => any;
    /**
     * 下拉刷新自定义区域，通过 refreshDone 结束刷新
     * @deprecated 推荐使用 `:pullRefresh="asyncFn"` 传入异步函数，组件自动管理刷新状态
     */
    pullArea?: (props: { refreshDone: () => void }) => any;
    footer?: () => any;
    footerLeft?: () => any;
    footerRight?: () => any;
  }>();

  const { query } = useRoute();
  const isSubPage = computed(() =>
    query.isSubPage === '0' ? false : query.isSubPage === '1' || props.isSubPage,
  );

  provide(PageHasFooterKey, props.showFooter);

  const pageTitle = computed(() => {
    const { meta, name = '' } = router.currentRoute.value;
    return props.title || meta?.title || name?.toString() || '';
  });

  const refreshing = ref(true);
  const emit = defineEmits(['refresh']);
  const onPullRefresh = async () => {
    refreshing.value = true;

    if (typeof props.pullRefresh !== 'function') return emit('refresh');

    await props.pullRefresh();
    refreshing.value = false;
  };
  /** @deprecated 推荐使用 `:pullRefresh="asyncFn"` 传入异步函数，组件自动管理 refreshing 状态 */
  const refreshDone = () => (refreshing.value = false);

  const wrapRef = useTemplateRef('wrapRef');
  let scrollEl: HTMLElement | null = null;
  let hasScrolled = false;

  const isRefreshObj = (v: typeof props.pullRefresh) => typeof v === 'object';

  const onTouchStart = (e: TouchEvent) => {
    hasScrolled = false;
    scrollEl = null;

    // noTouch 模式：禁止 pullArea 内触摸冒泡到 PullRefresh
    if (isRefreshObj(props.pullRefresh) && props.pullRefresh.noTouch) {
      e.stopPropagation();
      return;
    }

    scrollEl = findScrollingElement(e.target as HTMLElement, wrapRef.value);
    if (scrollEl && scrollEl.scrollTop > 1) {
      hasScrolled = true;
      e.stopPropagation();
    }
  };

  const onTouchMove = (e: TouchEvent) => {
    if (isRefreshObj(props.pullRefresh) && props.pullRefresh.noTouch) {
      e.stopPropagation();
      return;
    }
    if (!scrollEl) return;

    if (scrollEl.scrollTop > 1) {
      hasScrolled = true;
      e.stopPropagation();
      return;
    }
    if (hasScrolled) e.stopPropagation();
  };

  defineExpose({ refreshDone });

  const footerProps = computed(() => {
    return typeof props.showFooter === 'boolean' ? {} : props.showFooter;
  });
</script>

<style lang="less">
  :root .lb-pull-refresh {
    --lb-pull-refresh-head-height: @header-height;
  }

  .mts-pad {
    .mts-page-container {
      display: flex;
      flex-direction: column;
      width: 100%;
      max-width: fill-available;
      max-width: -webkit-fill-available;
      min-height: 100vh;
    }

    .mts-page-content {
      height: calc(100vh - @header-height);
      padding: 0 4px;

      &.has-footer {
        height: calc(100vh - @header-height - 60px);
      }
    }
  }
</style>
